import type { ISerialize } from '@/objects/ISerialize';
import { FileArchiveItem } from './FileArchiveItem';
import type { StatusCode } from './../enums/StatusCode';

export class ContestantImportRowStatus implements ISerialize {
    public AverageTimePrRow: number | null;

    public CurrentRow: number;

    public ElapsedTime: number | null;

    public FailedRows: number;

    public FailedRowsFile: FileArchiveItem | null;

    public GUID: string;

    public RowId: number;

    public Status: string;

    public StatusCode: StatusCode;

    public TotalRows: number;

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        AverageTimePrRow?: number | null;
        CurrentRow?: number;
        ElapsedTime?: number | null;
        FailedRows?: number;
        FailedRowsFile?: FileArchiveItem | null;
        GUID?: string | null;
        RowId?: number;
        Status?: string | null;
        StatusCode?: StatusCode;
        TotalRows?: number;
    }) {
        this.AverageTimePrRow = values.AverageTimePrRow ?? null;

        this.CurrentRow = values.CurrentRow ?? 0;

        this.ElapsedTime = values.ElapsedTime ?? null;

        this.FailedRows = values.FailedRows ?? 0;

        this.FailedRowsFile = values.FailedRowsFile ?? null;

        this.GUID = values.GUID ?? '';

        this.RowId = values.RowId ?? 0;

        this.Status = values.Status ?? '';

        this.StatusCode = values.StatusCode ?? 'Info';

        this.TotalRows = values.TotalRows ?? 0;
    }

    public serialize(): string {
        return (
            '{' +
            `"AverageTimePrRow": ${JSON.stringify(this.AverageTimePrRow)}` +
            ',' +
            `"CurrentRow": ${JSON.stringify(this.CurrentRow)}` +
            ',' +
            `"ElapsedTime": ${JSON.stringify(this.ElapsedTime)}` +
            ',' +
            `"FailedRows": ${JSON.stringify(this.FailedRows)}` +
            ',' +
            `"FailedRowsFile": ${!this.FailedRowsFile ? null : this.FailedRowsFile?.serialize()}` +
            ',' +
            `"GUID": ${JSON.stringify(this.GUID)}` +
            ',' +
            `"RowId": ${JSON.stringify(this.RowId)}` +
            ',' +
            `"Status": ${JSON.stringify(this.Status)}` +
            ',' +
            `"StatusCode": ${this.StatusCode == 'Info' ? 0 : this.StatusCode == 'Warning' ? 1 : this.StatusCode == 'Error' ? 2 : this.StatusCode == 'Success' ? 3 : this.StatusCode == 'None' ? 4 : 0}` +
            ',' +
            `"TotalRows": ${JSON.stringify(this.TotalRows)}` +
            '}'
        );
    }

    /**
     * Creates an instance of ContestantImportRowStatus from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        AverageTimePrRow?: number | null;
        CurrentRow?: number;
        ElapsedTime?: number | null;
        FailedRows?: number;
        FailedRowsFile?: FileArchiveItem | null;
        GUID?: string | null;
        RowId?: number;
        Status?: string | null;
        StatusCode?: StatusCode;
        TotalRows?: number;
    }): ContestantImportRowStatus {
        return new ContestantImportRowStatus(values);
    }

    public static _FromServerJson(json: any): ContestantImportRowStatus {
        return new ContestantImportRowStatus({
            AverageTimePrRow: json.AverageTimePrRow ?? null,
            CurrentRow: json.CurrentRow,
            ElapsedTime: json.ElapsedTime ?? null,
            FailedRows: json.FailedRows,
            FailedRowsFile: json.FailedRowsFile ? FileArchiveItem._FromServerJson(json.FailedRowsFile) : null,
            GUID: json.GUID ?? '',
            RowId: json.RowId,
            Status: json.Status ?? '',
            StatusCode:
                json.StatusCode == 0
                    ? 'Info'
                    : json.StatusCode == 1
                      ? 'Warning'
                      : json.StatusCode == 2
                        ? 'Error'
                        : json.StatusCode == 3
                          ? 'Success'
                          : json.StatusCode == 4
                            ? 'None'
                            : 'Info',
            TotalRows: json.TotalRows,
        });
    }

    /**
     * Creates an instance of ContestantImportRowStatus from another instance of ContestantImportRowStatus.
     * @param other The ContestantImportRowStatus you want to clone
     * @returns A cloned ContestantImportRowStatus
     */
    public static FromOther(other: ContestantImportRowStatus): ContestantImportRowStatus {
        return ContestantImportRowStatus._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return ContestantImportRowStatus.FromOther(this);
    }

    static NAME_OF_CLASS = 'ContestantImportRowStatus';
}
